/**
 * Analysis Service - سرویس تحلیل پوست
 * این سرویس مسئول ارسال نوتیفیکیشن‌های مربوط به تحلیل پوست است
 */

import { pushService } from '../push/pushService';

class AnalysisService {
  constructor() {
    this.scheduledNotifications = new Map();
  }

  /**
   * Send analysis complete notification
   * ارسال نوتیفیکیشن اتمام تحلیل
   * 
   * @param {Object} analysisData - داده‌های تحلیل
   * @param {string} userId - شناسه کاربر
   * @returns {Promise<boolean>}
   */
  async sendComplete(analysisData = {}, userId = null) {
    try {
      console.log('✅ Sending analysis complete notification...');
      
      const { 
        score = 0, 
        analysisId = null,
        message = 'تحلیل پوست شما آماده است!'
      } = analysisData;
      
      const result = await pushService.showLocalNotification(
        '✨ تحلیل پوست شما آماده شد!',
        {
          body: `امتیاز سلامت پوست شما: ${score}/100\n${message}`,
          data: {
            type: 'analysis-complete',
            url: analysisId ? `/analysis/${analysisId}` : '/profitscan',
            analysisId,
            userId,
            score,
            timestamp: Date.now(),
          },
          actions: [
            { action: 'view', title: 'مشاهده نتایج', icon: '/logo_pwa.png' },
            { action: 'dismiss', title: 'بستن' },
          ],
        },
        'analysis'
      );
      
      if (result) {
        console.log('✅ Analysis complete notification sent');
        this.trackNotification('complete', userId, analysisId);
      }
      
      return result;
    } catch (error) {
      console.error('❌ Failed to send analysis complete notification:', error);
      return false;
    }
  }

  /**
   * Send analysis in progress notification
   * ارسال نوتیفیکیشن در حال تحلیل
   * 
   * @param {string} userId - شناسه کاربر
   * @returns {Promise<boolean>}
   */
  async sendInProgress(userId = null) {
    try {
      console.log('⏳ Sending analysis in progress notification...');
      
      const result = await pushService.showLocalNotification(
        '⏳ در حال تحلیل پوست شما...',
        {
          body: 'لطفاً صبر کنید، هوش مصنوعی ما در حال بررسی پوست شماست.',
          data: {
            type: 'analysis-progress',
            url: '/scanpage',
            userId,
            timestamp: Date.now(),
          },
        },
        'analysis'
      );
      
      if (result) {
        console.log('✅ Analysis in progress notification sent');
      }
      
      return result;
    } catch (error) {
      console.error('❌ Failed to send analysis in progress notification:', error);
      return false;
    }
  }

  /**
   * Schedule analysis reminder
   * برنامه‌ریزی یادآوری تحلیل
   * 
   * @param {number} delay - تأخیر به میلی‌ثانیه (پیش‌فرض: 7 روز)
   * @param {string} userId - شناسه کاربر
   * @returns {Promise<number>} - شناسه notification برنامه‌ریزی شده
   */
  async scheduleReminder(delay = 7 * 24 * 60 * 60 * 1000, userId = null) {
    try {
      console.log(`⏰ Scheduling analysis reminder for ${delay}ms...`);
      
      const timeoutId = await pushService.scheduleNotification(
        '🔄 زمان تحلیل مجدد پوست!',
        {
          body: 'یک هفته از آخرین تحلیل شما گذشته است. برای بهترین نتایج، پوست خود را مجدداً اسکن کنید!',
          data: {
            type: 'analysis-reminder',
            url: '/scanpage',
            userId,
            timestamp: Date.now(),
          },
          actions: [
            { action: 'scan', title: 'اسکن جدید', icon: '/logo_pwa.png' },
            { action: 'dismiss', title: 'بعداً' },
          ],
        },
        delay,
        'reminder'
      );
      
      if (timeoutId) {
        this.scheduledNotifications.set(timeoutId, {
          type: 'analysis-reminder',
          userId,
          scheduledAt: Date.now(),
        });
        console.log(`✅ Analysis reminder scheduled with ID: ${timeoutId}`);
      }
      
      return timeoutId;
    } catch (error) {
      console.error('❌ Failed to schedule analysis reminder:', error);
      return null;
    }
  }

  /**
   * Cancel scheduled notification
   * لغو نوتیفیکیشن برنامه‌ریزی شده
   * 
   * @param {number} notificationId - شناسه notification
   * @returns {boolean}
   */
  cancel(notificationId) {
    try {
      if (this.scheduledNotifications.has(notificationId)) {
        pushService.cancelScheduledNotification(notificationId);
        this.scheduledNotifications.delete(notificationId);
        console.log(`✅ Analysis notification cancelled: ${notificationId}`);
        return true;
      }
      return false;
    } catch (error) {
      console.error('❌ Failed to cancel analysis notification:', error);
      return false;
    }
  }

  /**
   * Cancel all scheduled notifications
   * لغو همه نوتیفیکیشن‌های برنامه‌ریزی شده
   * 
   * @returns {number} - تعداد نوتیفیکیشن‌های لغو شده
   */
  cancelAll() {
    try {
      let count = 0;
      for (const [notificationId] of this.scheduledNotifications) {
        if (this.cancel(notificationId)) {
          count++;
        }
      }
      console.log(`✅ Cancelled ${count} analysis notifications`);
      return count;
    } catch (error) {
      console.error('❌ Failed to cancel all analysis notifications:', error);
      return 0;
    }
  }

  /**
   * Track notification sent
   * ثبت نوتیفیکیشن ارسال شده
   * 
   * @param {string} type - نوع نوتیفیکیشن
   * @param {string} userId - شناسه کاربر
   * @param {string} analysisId - شناسه تحلیل
   */
  trackNotification(type, userId, analysisId = null) {
    try {
      const key = `analysis_notification_${type}_${analysisId || 'latest'}`;
      const data = {
        userId,
        analysisId,
        sentAt: Date.now(),
        type,
      };
      localStorage.setItem(key, JSON.stringify(data));
      console.log(`📊 Tracked analysis notification: ${type}`);
    } catch (error) {
      console.error('❌ Failed to track notification:', error);
    }
  }
}

// Export singleton instance
export const analysisService = new AnalysisService();

export default analysisService;



